/**
 * Что можно делать на каждом этапе 2D-чертежа.
 *
 * Этап — это не только какие слои видны, но и с чем сейчас работают:
 *   • «Исходный план», «Демонтаж», «Монтаж» — стены и проёмы; каталога, кабелей и воды нет;
 *   • «Мебель», «Пол», «Гидроизоляция», «Сантехника», «Электрика» — объекты в готовых стенах:
 *     стены и проёмы не правятся и не выделяются, кабели — только на «Электрике»,
 *     трубы — только на «Сантехнике»;
 *   • «Все слои» — без ограничений.
 * Одно правило на весь редактор: по нему гаснут кнопки рейки, не включаются инструменты
 * (и горячими клавишами), а холст не даёт выделить и сдвинуть стену или проём.
 */
import { DrawerSection, Tool, type PlanStage } from "./editorTypes";

/** Род работы, который этап разрешает или запрещает. */
export type PlanStageFeature = "walls" | "openings" | "catalog" | "wiring" | "water";

const WALL_STAGES: readonly PlanStage[] = ["initial", "demolition", "construction"];
const OBJECT_STAGES: readonly PlanStage[] = [
	"furniture",
	"floor",
	"waterproof",
	"plumbing",
	"electrical",
];

/** Подпись этапа для подсказки «недоступно на этапе …». */
export const PLAN_STAGE_LABELS: Record<PlanStage, string> = {
	all: "Все слои",
	initial: "Исходный план",
	demolition: "Демонтаж",
	construction: "Монтаж",
	furniture: "Мебель",
	floor: "Пол",
	waterproof: "Гидроизоляция",
	plumbing: "Сантехника",
	electrical: "Электрика",
};

export const planStageAllows = (stage: PlanStage, feature: PlanStageFeature): boolean => {
	if (stage === "all") return true;
	switch (feature) {
		case "walls":
		case "openings":
			return WALL_STAGES.includes(stage);
		case "catalog":
			return OBJECT_STAGES.includes(stage);
		case "wiring":
			return stage === "electrical";
		case "water":
			return stage === "plumbing";
	}
};

/** К чему относится раздел левой панели; null — раздел есть на любом этапе. */
const DRAWER_FEATURE: Partial<Record<DrawerSection, PlanStageFeature>> = {
	[DrawerSection.Walls]: "walls",
	[DrawerSection.Openings]: "openings",
	[DrawerSection.Fixtures]: "catalog",
	[DrawerSection.Wiring]: "wiring",
	[DrawerSection.Water]: "water",
};

/** К чему относится инструмент; без записи — инструмент есть на любом этапе. */
const TOOL_FEATURE: Partial<Record<Tool, PlanStageFeature>> = {
	[Tool.Wall]: "walls",
	[Tool.Room]: "walls",
	[Tool.UnderlayScale]: "walls",
	[Tool.UnderlayMove]: "walls",
	[Tool.Door]: "openings",
	[Tool.Window]: "openings",
	[Tool.Place]: "catalog",
	[Tool.LightingConnection]: "catalog",
	[Tool.Cable]: "wiring",
	[Tool.Pipe]: "water",
};

/** Раздел левой панели, к которому относится инструмент; undefined — инструмент общий (выбор, ластик). */
export const toolFeature = (tool: Tool): PlanStageFeature | undefined => TOOL_FEATURE[tool];

/** Род работы раздела левой панели; undefined — раздел без своих инструментов (слои, инфо, отделка). */
export const drawerFeature = (drawer: DrawerSection | null): PlanStageFeature | undefined =>
	drawer === null ? undefined : DRAWER_FEATURE[drawer];

export const planStageAllowsDrawer = (stage: PlanStage, drawer: DrawerSection | null): boolean => {
	const feature = drawer === null ? undefined : DRAWER_FEATURE[drawer];
	return feature === undefined || planStageAllows(stage, feature);
};

export const planStageAllowsTool = (stage: PlanStage, tool: Tool): boolean => {
	const feature = TOOL_FEATURE[tool];
	return feature === undefined || planStageAllows(stage, feature);
};
